<template>
    <div class="sepulture-types-page">
        <a-spin tip="Carregando..." :spinning="spinning">
            <a-config-provider :locale="currentLocale">
                <a-card>
                    <template #title>
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <span>Tipos de Sepultura</span>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item>Listar</a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template #extra>
                        <a-button :icon="h(PlusOutlined)" @click="openModal()">Novo Registro</a-button>
                    </template>

                    <div class="table-toolbar">
                        <a-input-search
                            v-model:value="search"
                            placeholder="Filtrar por nome ou descrição"
                            allow-clear
                            :enter-button="false"
                            style="max-width: 320px"
                            @input="applyFilter"
                        />
                    </div>

                    <a-table
                        :columns="columns"
                        :data-source="filteredData"
                        :row-key="(record) => record.id"
                        bordered
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.dataIndex === 'image'">
                                <div v-if="record.client_image || record.clientImage" class="table-image-cell">
                                    <div class="table-thumb-wrapper">
                                        <img
                                            class="table-thumb-img"
                                            :src="normalizeImageSrc((record.client_image || record.clientImage)?.path)"
                                            :alt="(record.client_image || record.clientImage)?.name"
                                            @error="onImgError"
                                        />
                                    </div>
                                    <span class="table-image-name">{{ (record.client_image || record.clientImage)?.name }}</span>
                                </div>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template v-if="column.key === 'acao'">
                                <a-tooltip title="Alterar">
                                    <a-button type="primary" class="center-flex" shape="circle" @click="openModal(record)">
                                        <template #icon>
                                            <FormOutlined />
                                        </template>
                                    </a-button>
                                </a-tooltip>
                                <a-popconfirm
                                    :title="'Tem certeza que deseja excluir \'' + record.name + '\'?'"
                                    ok-text="Excluir"
                                    cancel-text="Cancelar"
                                    :ok-button-props="{ danger: true }"
                                    @confirm="destroy(record)"
                                >
                                    <template #icon><QuestionCircleOutlined style="color: #ff4d4f" /></template>
                                    <a-tooltip title="Excluir">
                                        <a-button type="primary" class="center-flex" danger shape="circle">
                                            <template #icon>
                                                <DeleteOutlined />
                                            </template>
                                        </a-button>
                                    </a-tooltip>
                                </a-popconfirm>
                            </template>
                        </template>
                    </a-table>
                </a-card>
            </a-config-provider>
        </a-spin>

        <a-modal
            v-model:open="modalVisible"
            :title="editingId ? 'Editar Tipo de Sepultura' : 'Novo Tipo de Sepultura'"
            :mask-closable="false"
            @ok="submitForm"
            @cancel="closeModal"
        >
            <a-form layout="vertical" :model="form">
                <a-form-item label="Nome" required>
                    <a-input v-model:value="form.name" placeholder="Nome do tipo" />
                </a-form-item>
                <a-form-item label="Descrição">
                    <a-textarea v-model:value="form.description" placeholder="Descrição (opcional)" :rows="3" />
                </a-form-item>
                <a-form-item label="Imagem">
                    <div class="thumb-line">
                        <div v-if="selectedImagePath" class="thumb-wrapper">
                            <img class="thumb-img" :src="selectedImagePath" alt="Imagem" @error="onImgError" />
                        </div>
                        <a-space>
                            <a-button @click="showImagePicker = true">Selecionar</a-button>
                            <a-button v-if="form.image != null" @click="form.image = undefined">Limpar</a-button>
                        </a-space>
                    </div>
                </a-form-item>
            </a-form>
        </a-modal>

        <a-modal
            v-model:open="showImagePicker"
            title="Selecionar imagem"
            :footer="null"
            destroy-on-close
            @cancel="showImagePicker = false"
        >
            <ClientImagesPicker
                :selected-image-path="selectedImagePathForPicker"
                @save="handleImageSave"
                @cancel="showImagePicker = false"
            />
        </a-modal>
    </div>
</template>

<script setup>
import { ref, h, onMounted, computed, defineAsyncComponent } from 'vue';
import { PlusOutlined, FormOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import ptBR from 'ant-design-vue/es/locale/pt_BR';
import { message } from 'ant-design-vue';

const ClientImagesPicker = defineAsyncComponent(() =>
    import('../Components/ClientImagesPicker.vue')
);

const route = '/api/cemeteries/sepulture-types';
const currentLocale = ref(ptBR);
const spinning = ref(false);
const search = ref('');
const dataSource = ref([]);
const modalVisible = ref(false);
const showImagePicker = ref(false);
const editingId = ref(null);
const clientImages = ref([]);
/** Path da imagem selecionada no picker (para miniatura imediata) */
const selectedImagePathDisplay = ref(null);

const form = ref({
    name: '',
    description: '',
    image: undefined,
});

const columns = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        sorter: (a, b) => a.id - b.id,
    },
    {
        title: 'Nome',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
        title: 'Descrição',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
    },
    {
        title: 'Imagem',
        dataIndex: 'image',
        key: 'image',
        width: 220,
    },
    {
        title: 'Ação',
        key: 'acao',
        width: 120,
    },
];

const clientImagesOptions = computed(() =>
    clientImages.value.map((img) => ({ id: img.id, name: img.name || `Imagem #${img.id}` }))
);

const selectedImagePathForPicker = computed(() => {
    if (form.value.image == null) return null;
    const img = clientImages.value.find((i) => i.id === form.value.image);
    return img?.path ?? null;
});

function normalizeImageSrc(path) {
    if (!path || typeof path !== 'string') return path;
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('/')) return path;
    return '/' + path.replace(/^\//, '');
}

const selectedImagePath = computed(() => {
    const raw = selectedImagePathDisplay.value || (clientImages.value.find((i) => i.id === form.value.image)?.path ?? null);
    return normalizeImageSrc(raw);
});

function handleImageSave(payload) {
    const id = typeof payload === 'object' && payload !== null && payload.id != null ? payload.id : null;
    const path = typeof payload === 'object' && payload !== null && payload.path != null ? payload.path : (typeof payload === 'string' ? payload : null);
    if (id != null) {
        form.value.image = id;
        selectedImagePathDisplay.value = path || (clientImages.value.find((i) => i.id === id)?.path ?? null);
    } else if (path) {
        const img = clientImages.value.find((i) => i.path === path);
        form.value.image = img ? img.id : undefined;
        selectedImagePathDisplay.value = img ? path : null;
    }
    showImagePicker.value = false;
}

function onImgError(ev) {
    ev.target.src =
        'data:image/svg+xml;utf8,' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="120">' +
        '<rect width="100%" height="100%" fill="%23f5f5f5"/>' +
        '<text x="50%" y="50%" font-size="12" text-anchor="middle" fill="%23999" dy=".3em">sem preview</text>' +
        '</svg>';
}

const filteredData = computed(() => {
    if (!search.value?.trim()) return dataSource.value || [];
    const term = search.value.trim().toLowerCase();
    return (dataSource.value || []).filter(
        (row) =>
            (row.name && row.name.toLowerCase().includes(term)) ||
            (row.description && row.description.toLowerCase().includes(term))
    );
});

function applyFilter() {}

function openModal(record = null) {
    selectedImagePathDisplay.value = null;
    editingId.value = record ? record.id : null;
    form.value = {
        name: record ? record.name : '',
        description: record ? record.description ?? '' : '',
        image: record && record.image != null ? record.image : undefined,
    };
    modalVisible.value = true;
}

function closeModal() {
    modalVisible.value = false;
    editingId.value = null;
    selectedImagePathDisplay.value = null;
    form.value = { name: '', description: '', image: undefined };
}

async function loadClientImages() {
    try {
        const { data } = await window.axios.get('/api/configurations/client-images');
        clientImages.value = data.data || data || [];
    } catch {
        clientImages.value = [];
    }
}

async function load() {
    try {
        spinning.value = true;
        const response = await window.axios.get(route);
        dataSource.value = response.data.data || [];
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao carregar tipos de sepultura.');
    } finally {
        spinning.value = false;
    }
}

async function submitForm() {
    if (!form.value.name?.trim()) {
        message.warning('Informe o nome.');
        return;
    }
    try {
        spinning.value = true;
        const payload = {
            name: form.value.name.trim(),
            description: form.value.description?.trim() || null,
            image: form.value.image != null && form.value.image !== '' ? Number(form.value.image) : null,
        };
        if (editingId.value) {
            await window.axios.put(`${route}/${editingId.value}`, payload);
            message.success('Alterado com sucesso!');
        } else {
            await window.axios.post(route, payload);
            message.success('Criado com sucesso!');
        }
        closeModal();
        await load();
    } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.errors?.name?.[0] || err.message;
        message.error(msg || 'Erro ao salvar.');
    } finally {
        spinning.value = false;
    }
}

async function destroy(record) {
    try {
        spinning.value = true;
        const response = await window.axios.delete(`${route}/${record.id}`);
        message.success(response.data?.message || 'Excluído com sucesso!');
        await load();
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao excluir.');
    } finally {
        spinning.value = false;
    }
}

onMounted(async () => {
    await Promise.all([load(), loadClientImages()]);
});
</script>

<style scoped>
.sepulture-types-page {
    padding: 24px;
}
.table-toolbar {
    margin-bottom: 16px;
}
.center-flex {
    display: inline-flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
}
.text-muted {
    color: #999;
}
.anticon {
    vertical-align: 2px !important;
}
.table-image-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}
.table-thumb-wrapper {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
}
.table-thumb-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
.table-image-name {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.thumb-line {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.thumb-wrapper {
    width: 160px;
    height: 120px;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    overflow: hidden;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
}
.thumb-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
</style>
